import {
    DataWithLocation,
    DataWithLocationArray,
    GetWithLocationFilterArgs,
    ImsTimeCollection,
} from '../../../shared/collections/time/ims_time_collection.factory';
import { CachedEventRegistry } from '../../../shared/events/cached_event_registry';
import { VectorHeatMapContext } from '../../../views/view_types/view_utils';
import { estimateIterations } from './GeoDataServiceHelpers';
import moment from 'moment-timezone';
import { clone } from 'lodash';
import { HeatmapDataRequester } from './HeatmapDataRequester';

/**
 * Generates heat map data for the given filters.
 *
 *  This works by requesting the full data set, limited by the maxPoints value, until the full data set is retrieved.
 *  For example:
 *   If the heatmap timeframe is 24 hours, the maxPoints is 250,000, and the full data set contains 250,000 data
 *   points, then this function should make around ten calls to the API if we have a chunk size of 25 000. Each call will add the data to the full set
 *   and change the filter's end timestamp to be the earliest timestamp in the returned set. This means the data
 *   will fill in descending order.
 *
 *   Pseudo SQL - Req 1: SELECT points from 9am-4pm LIMIT 50 000 ORDER Desc.
 *   Pseudo SQL - Req 2: SELECT points from 9am-2:xx pm LIMIT 50 000 ORDER Desc. (xx - is latest time returned from req 1)
 *   etc.... until 300 000 (max points) or all points in time range are filled
 *
 *   heatmapChunkSize (max amount of points to retrieve per request - set in config.js): 50 000
 *   maxHeatMapPoints (total amount of points to be retrieved - set in config.js): 300 000
 *   heatmap points in the db: 245000
 *
 *       frontend request 1
 *       +-------------------------------------------------->
 *
 *       frontend request 2
 *       +---------------------------------------->
 *
 *       frontend request 3
 *       +----------------------------->
 *
 *       frontend request 4
 *       +------------------->
 *
 *       frontend request 5
 *       +---------->
 *
 *       +---------+---------+---------+----------+----------+         If a single request fails.
 *       |         |         |         |          |          |         Retry it a few times!
 *  9am  | 45 000p | 50 000p | 50 000p | 50 000p  | 50 000p  | 4pm
 *       |         |         |         |          |          |         If we've failed requests too many
 *       |  req5   |  req4   |  req3   |   req2   |   req1   |         times, just bail on the heatmap request
 *       +---------+---------+---------+----------+----------+
 *                 end time  end time  end time   end time   end time
 *                 of req5   of req4   of req3    of req2    of req1
 *
 *          <---------------------------------------------+
 *          API fills away from end time (descending)
 *
 *
 */

export class LimitChunkedHeatmapDataRequester implements HeatmapDataRequester {
    private _heatMapChunkSize: number;
    private _columnName: string;
    private _filterArgs: GetWithLocationFilterArgs;
    private _timeCollection: ImsTimeCollection;
    private _hoursRequested: number;
    private _heatmapListeners: CachedEventRegistry;
    private _totalOfFailedChunkRequests: number;
    private _reachedLimitOfTotalFailedChunks: boolean;
    private _chunksThatHaveFailed: Array<number>;
    private _maxPoints: number;
    private _heatMapDatabaseFailLimitPerChunk: number;
    private _heatMapDatabaseFailLimit: number;
    private _vectorHeatMapContext: VectorHeatMapContext | null;

    constructor(
        columnName: string,
        filterArgs: GetWithLocationFilterArgs,
        timeCollection: ImsTimeCollection,
        hoursRequested: number,
        heatmapListeners: CachedEventRegistry,
        maxPoints: number,
        heatmapChunkSize: number,
        heatMapDatabaseFailLimitPerChunk: number,
        heatMapDatabaseFailLimit: number,
        vectorHeatMapContext: VectorHeatMapContext | null,
    ) {
        this._columnName = columnName;
        this._filterArgs = filterArgs;
        this._timeCollection = timeCollection;
        this._hoursRequested = hoursRequested;
        this._maxPoints = maxPoints;
        this._heatmapListeners = heatmapListeners;
        this._totalOfFailedChunkRequests = 0;
        this._reachedLimitOfTotalFailedChunks = false;
        this._chunksThatHaveFailed = [];
        this._heatMapDatabaseFailLimitPerChunk = heatMapDatabaseFailLimitPerChunk;
        this._heatMapDatabaseFailLimit = heatMapDatabaseFailLimit;
        this._heatMapChunkSize = heatmapChunkSize;
        this._vectorHeatMapContext = vectorHeatMapContext;
    }

    async makeHeatMapRequests(): Promise<DataWithLocationArray> {
        let combinedDataWithLocation = {} as DataWithLocation;
        let estimatedNumberOfIterations;
        const filterArgs = clone(this._filterArgs);

        let chunkNumber = 1;
        while (_shouldMakeAnotherRequest(combinedDataWithLocation, this._maxPoints)) {
            const chunkOfDataWithLocations = await this._fetchDataFromTimeCollection(
                clone(filterArgs),
                chunkNumber,
            );
            if (_noMoreData(chunkOfDataWithLocations)) {
                break;
            }
            if (_isFirstChunkRequest(chunkNumber)) {
                //this is where we take a stab at the progress bar
                estimatedNumberOfIterations = await estimateIterations(
                    this._timeCollection,
                    chunkOfDataWithLocations,
                    this._hoursRequested,
                    this._maxPoints,
                    this._heatMapChunkSize,
                );
            }

            // update the filter args for the next request.
            filterArgs.endTime =
                await this._getTimestampOfLastDatapointReturned(chunkOfDataWithLocations);

            combinedDataWithLocation = this._addFetchedChunkToCombinedDataWithLocation(
                chunkOfDataWithLocations,
                combinedDataWithLocation,
            );

            this._heatmapListeners.notifyListeners(
                _calculateProgress(chunkNumber, estimatedNumberOfIterations),
            );

            if (this._stillHaveDataToFetch(chunkOfDataWithLocations)) {
                chunkNumber++;
            } else {
                break;
            }
        }
        return this._buildCombinedDataWithLocationArray(combinedDataWithLocation);
    }

    private async _fetchDataFromTimeCollection(
        filterArgs: GetWithLocationFilterArgs,
        chunkNumber: number,
    ): Promise<DataWithLocation | any> {
        let chunkFailCounter = 0;

        while (this._shouldRetryDatabaseFetch(chunkFailCounter)) {
            // We only want to try each chunk a certain amount of times (set in configService)
            // We want to limit the number of fails for all requests (set in configService)
            try {
                return await this._timeCollection.getWithLocation(
                    this._columnName,
                    filterArgs,
                    this._heatMapChunkSize,
                    this._vectorHeatMapContext,
                );
            } catch (e) {
                console.warn('failed heatmap data chunk request, retrying');
                this._totalOfFailedChunkRequests++;
            }
            chunkFailCounter++;
        }

        return this._updateFailureCounters(chunkFailCounter, chunkNumber);
    }

    private _updateFailureCounters(chunkFailCounter: number, chunkNumber: number): void {
        if (this._reachedChunkRetryLimit(chunkFailCounter)) {
            this._chunksThatHaveFailed.push(chunkNumber);
        }
        this._reachedLimitOfTotalFailedChunks = this._reachedTotalFailedChunks();
    }

    private _buildCombinedDataWithLocationArray(
        combinedDataWithLocation: DataWithLocation,
    ): DataWithLocationArray {
        const combinedDataWithLocationArray = [];
        for (const key in combinedDataWithLocation) {
            if (combinedDataWithLocationArray.length < this._maxPoints) {
                combinedDataWithLocationArray.push(combinedDataWithLocation[key]);
            }
        }
        return combinedDataWithLocationArray;
    }

    private async _getTimestampOfLastDatapointReturned(
        chunkOfDataWithLocation: DataWithLocationArray,
    ): Promise<string> {
        const lastPointReturned = await this._timeCollection.getAsync(
            chunkOfDataWithLocation[chunkOfDataWithLocation.length - 1].id,
        );
        if (lastPointReturned) {
            return moment(lastPointReturned.resource.timestamp).toISOString();
        } else {
            throw new Error("Can't find the last fetched point in the collection");
        }
    }

    private _addFetchedChunkToCombinedDataWithLocation(
        chunkOfDataWithLocation: DataWithLocationArray,
        allDataWithLocation: DataWithLocation,
    ): DataWithLocation {
        chunkOfDataWithLocation.map((dataChunk) => {
            allDataWithLocation[dataChunk.id] = dataChunk;
        });
        return allDataWithLocation;
    }

    private _reachedTotalFailedChunks() {
        return this._totalOfFailedChunkRequests === this._heatMapDatabaseFailLimit;
    }

    private _reachedChunkRetryLimit(chunkRetries: number): boolean {
        return chunkRetries === this._heatMapDatabaseFailLimitPerChunk;
    }

    private _shouldRetryDatabaseFetch(counter: number): boolean {
        return !this._reachedTotalFailedChunks() && !this._reachedChunkRetryLimit(counter);
    }

    private _stillHaveDataToFetch(chunkOfDataWithLocation: DataWithLocationArray): boolean {
        return chunkOfDataWithLocation.length === this._heatMapChunkSize;
    }

    get reachedLimitOfTotalFailedChunks(): boolean {
        return this._reachedLimitOfTotalFailedChunks;
    }

    get chunksThatHaveFailed(): Array<number> {
        return this._chunksThatHaveFailed;
    }
}

function _noMoreData(chunkOfDataWithLocations: DataWithLocationArray): boolean {
    return !chunkOfDataWithLocations || !chunkOfDataWithLocations.length;
}

function _calculateProgress(fetchCounter: number, estimatedNumberOfIterations: number): number {
    return Math.round((fetchCounter / estimatedNumberOfIterations) * 100);
}

function _shouldMakeAnotherRequest(
    combinedDataWithLocation: DataWithLocation,
    maxPoints: number,
): boolean {
    return Object.keys(combinedDataWithLocation).length < maxPoints;
}

function _isFirstChunkRequest(fetchCounter: number) {
    return fetchCounter === 1;
}
